import { useEffect } from "react";

/**
 * Lightweight SEO hook — sets per-page <title>, meta description,
 * canonical URL and Open Graph tags. No external library needed.
 *
 * Also injects an sr-only <h1> at the top of the document via a temporary
 * DOM node if the caller sets `injectH1`, but prefers callers to render
 * their own visible H1 (this hook only handles head-level SEO).
 */
export default function useSEO({ title, description, path }) {
    useEffect(() => {
        if (title) document.title = title;

        const setMeta = (attr, key, value) => {
            if (!value) return;
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
        };

        setMeta("name", "description", description);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description);
        setMeta("property", "og:type", "website");
        setMeta("name", "twitter:title", title);
        setMeta("name", "twitter:description", description);

        // Canonical link
        if (path) {
            const url = `${window.location.origin}${path}`;
            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement("link");
                link.setAttribute("rel", "canonical");
                document.head.appendChild(link);
            }
            link.setAttribute("href", url);
            setMeta("property", "og:url", url);
        }
    }, [title, description, path]);
}
